/**
 * QAG ダッシュボードの KPI 定義レジストリ（実装側の正本）。
 *
 * ⚠️ 目標値・判定式・ラベルを **画面コンポーネントに直接書かないこと**。
 * ここだけを直せば全画面に反映される構造にしてある。
 *
 * 定義の出所は Obsidian の単一ノート（担当者ローカル環境の「思考/QualityAtelier/KPI定義レジストリ.md」、
 * チーム共有先は未整備）。
 * 設計上の根拠は aircloset-dashboard の docs/quality-atelier-kpi-design.md §3-0。
 * この3者の食い違いは Claude Code のスキル `/qag-kpi-sync` で検知・同期する（手動実行）。
 */

export type KpiId =
  | 'churnRate'
  | 'churnCount'
  | 'onboardingRate'
  | 'loyaltyRate'
  | 'csat'
  | 'firstResponseSlaRate'
  | 'answerSlaRate'
  | 'threeTurnRate'
  | 'resolutionRate'
  | 'aiCompletionRate';

export interface KpiDefinition {
  /** カード見出し */
  label: string;
  /** 軸ラベルに使う短い指標名 */
  metricName: string;
  unit: 'percent' | 'count';
  /** 良い方向。'up' = 高いほど良い */
  direction: 'up' | 'down';
  /**
   * 状態ゴール上の理想値（北極星）。CS の KPI4本だけが持ち、他は null。
   * ⚠ **達成/未達の判定には使わない。** 判定は `target`（今期の運用目標）に対してのみ行う。
   * 約束していない水準に「未達」と出すのは評価ではなくノイズになるため（2026-10-05）。
   */
  goal: number | null;
  /**
   * 今期の運用目標（SLO）。**判定に使う唯一の値**で、未設定なら null。
   * null のときバッジは出さず、`goal` との差分だけを見せる。
   * 2026-10-06 時点では CS の KPI4本とも `goal` と同値（100%）なので差分表示は出ない。
   * 段階目標を置きたくなったらここを下げるだけで切り替わる。
   */
  target: number | null;
  /** 四半期集約の方法 */
  aggregation: 'sum' | 'average';
  /** 判定の定義（画面の注記と設計書で同じ文言を使う） */
  definition: string;
  /** 実測ベースライン。画面に出す場合の文言 */
  baseline: string | null;
  /** 定義の出典（設計書のセクション） */
  source: string;
  /** 定義が確定した日 */
  decidedAt: string;
  /** 未決事項。あれば同期ルーティンが期限として扱う */
  pending: string | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business QAGダッシュボードの全KPI定義（ラベル・ゴール/今期の運用目標・判定式・集約方法・出典）を集約した正本レジストリ
 * @graph-connects none
 */
export const KPI_DEFINITIONS: Record<KpiId, KpiDefinition> = {
  churnRate: {
    label: '退会率',
    metricName: '退会率',
    unit: 'percent',
    direction: 'down',
    goal: null,
    target: null,
    aggregation: 'average',
    definition: '当月退会会員数 ÷ 月初在籍（ladies）。率は DOMO 公式定義を正とする',
    baseline: '直近12ヶ月平均 8.2%',
    source: 'quality-atelier-kpi-design.md §3-0 / QAG グループ KGI',
    decidedAt: '2026-08-13',
    pending: '目標値（絶対値でなく相対改善幅で置く方針）',
  },
  churnCount: {
    label: '退会者数',
    metricName: '退会者数',
    unit: 'count',
    direction: 'down',
    goal: null,
    target: null,
    aggregation: 'sum',
    definition:
      '退会率の分子。CRM が直接動かす数。対象はレディースのみ（メンズは含まない）で、カードの値は直近の確定月',
    baseline: null,
    source: 'quality-atelier-kpi-design.md §3-0 / チーム CRM 機能 KGI',
    decidedAt: '2026-08-13',
    pending: '目標値',
  },
  onboardingRate: {
    label: 'オンボ成功率（3ヶ月）',
    metricName: 'オンボ成功率',
    unit: 'percent',
    direction: 'up',
    goal: null,
    target: null,
    aggregation: 'average',
    definition:
      'DOMO カード CCG_登録日別各月突破率_KPI の オンボ成功率 と同じ判定。登録から31日刻みで数え、退会申請日が 94日未満なら未到達。申請から退会確定まで8日以上空いた場合は退会が次サイクル頭に効いたとみなし 63日で判定する。分母は users ∪ membership_histories の (user_id, 登録週) ペア（週は月曜起点）。確定まで94日ラグ',
    baseline:
      '2026-01 の登録週を人数加重して 46.3%（2025-09〜2026-03 は 37.6〜46.3%）。DOMO カードと7週で突き合わせ、平均絶対差 0.7pt で一致を確認済み',
    source:
      'quality-atelier-kpi-design.md §3-0 / DOMO オンボ成功率 = 1 - SUM(dead_at_third_month_user_1)/COUNT(user_id) を再現（2026-09-25）',
    decidedAt: '2026-09-25',
    pending: '目標値（相対改善幅で設定）',
  },
  loyaltyRate: {
    label: 'ロイヤル成功率（6ヶ月）',
    metricName: 'ロイヤル成功率',
    unit: 'percent',
    direction: 'up',
    goal: null,
    target: null,
    aggregation: 'average',
    definition:
      'DOMO カードの アダプション成功率 と同一（2026-09-25 にロイヤル＝アダプションと定義）。判定の形はオンボ成功率と同じで、境界が 156 / 187日になるだけ。分母・週の取り方も共通。確定まで187日ラグ',
    baseline: '2026-01 の登録週を人数加重して 28.3%（2025-09〜2026-03 は 25.2〜29.0%）',
    source:
      'quality-atelier-kpi-design.md §3-0 / DOMO アダプション成功率 = 1 - SUM(dead_at_sixth_month_user_1)/COUNT(user_id)（2026-09-25）',
    decidedAt: '2026-09-25',
    pending: '目標値（相対改善幅で設定）',
  },
  // 2026-09-29: KPI からモニタリングへ移した。2026-09-25 に KGI 指標へ格上げしたが4日で撤回している。
  // チャットでの測定母集団が全体の10.8%しかなく、AI完遂率を上げるほど痩せるため、
  // 主たる判定は行動ベース（resolutionRate）に任せ、CSAT は補助信号として見る。
  csat: {
    label: 'CSAT ｜ 満足度（全チャネル）',
    metricName: 'CSAT',
    unit: 'percent',
    direction: 'up',
    goal: null,
    target: 0.95,
    aggregation: 'average',
    definition:
      'お客様対応満足度。★2026-09-03 から対象を拡大し、メール・チャット・電話の全チャネルを合算して測る（旧: メール・チャットのみ）。2026-09-29 に KPI からモニタリングへ移し、解決率の補助信号として置く',
    baseline:
      'メール・チャットのみでの実測 95.2%。⚠ チャットでの測定母集団は全体の10.8%（AI完結8,458件に対し回答は1件）。アンケート配信で母集団を広げるのではなく、行動ベースの解決率で代替する方針',
    source: 'quality-atelier-kpi-design.md §3-0 / CS 指標構成の整理（2026-09-29）',
    decidedAt: '2026-09-29',
    pending: '電話を含めた全チャネルでの再計測（母集団が変わるため現在値は暫定）',
  },
  // 2026-09-25 世代交代。旧「5分以内返信率」（営業時間クロック・目標90%）を
  // 「初動SLO｜10秒以内の初回応答」（カレンダー時間）へ置き換えた。旧値と繋げてグラフ表示しない。
  // 2026-10-05: SLA → SLO へ改称し、2026-10-04 に入れた「目標100%」を取り下げた。
  // 罰則も対外的な契約も無いので SLA ではない（SLA は remedy を定めて初めて成立する語）。
  // ⚠ SLO と名乗ると目標100%と両立しない。原資がエラーバジェット（100% − SLO）なので、
  // 100% はバジェットがゼロ＝常に未達で改善の優先順位がつかず、外れ値1件と構造的な問題が
  // 同じ「未達」になる。特に初動は未達の93%が「AIが出ず最初から人待ち」だった（旧エクスポート）。
  // ⚠ 2026-10-08 撤回: ここを**構造的に100%到達不能**と書いていたのは誤り。原因は
  // 「ALFが必ず一次応答する設計に変えない限り届かない」と自分で書いておきながら、
  // 直せるもの（設計変更）を到達不能と結論づけていた。新テーブルでの未達は 4.9% で、
  // その99.3%が「botが一度も出なかった」＝**直せる対象**。
  // → 理想は goal（100%・北極星）、今期の運用目標は target に分けた（2026-10-05）。
  //    同日のうちに 3ターム解決率・解決率も同じ扱いへ揃え、いったん4本とも target 未設定にした。
  // 2026-10-06: **今期SLO（2027年6月期）を4本とも 100% に確定**（鷲澤）。goal と同値になるので、
  //    画面は再び「目標 100%」＋達成/未達バッジを出す。goal / target の分離は**形としては残す**
  //    （段階目標を置きたくなったら target 1行で切り替わる）。
  // ⚠ **100% はエラーバジェットがゼロ**なので、上記の弊害は承知のうえで受け入れている。
  //    ⚠ 2026-10-08 撤回: 「初動は構造的に100%到達不能」としていたが誤り。未達 4.9% の
  //    99.3%は「botが一度も出なかった」で、しかも**全件が team_id 未割当のチャット**に
  //    集中している（team_id 付きは未達ゼロ）。**直せるものなので 100% を取りに行く**
  //    （2026-10-08 方針）。残り3本については引き続き「残り pt の縮み方」を見る。
  // ⚠ ベースラインの出どころは Channel Talk の一括エクスポート。回答SLO は 2026-10-04 に
  // channeltalk.sla_responses へ移したので、2本を引き算しないこと（母集団が違う）。
  // 2026-10-08: 媒体を `medium = 'native'` に絞った。それまで `medium != 'email'` と
  // 書いていたため **Instagram（medium='app'）が混ざっていた**。`app` は aircloset の
  // モバイルアプリではなく Instagram Messenger（`contactMediumType` の
  // `appInstagramMessenger` に1対1対応。channel-talk-api-shim.d.ts の実測）。
  // `sla_target_sec` の対象は ['native','email','app'] で Instagram を含むので、
  // **判定済みテーブルの母集団をそのまま使わず指標ごとに絞る**。
  // 2026-10-08: **系列は 2025-07-01 より前を出さない**（API 側の `TIER2_COVERAGE_START`）。
  // Channel Talk Tier 2 の取り込みが始まった日で、remask ジョブの `--fetched-from` と一致する。
  // ここより前は単にデータが無い。初動SLO・回答SLOの両方に効く。
  // ⚠ いったん 2026-06-01 にしてしまった（PR #5091 → #5096 で修正）。cortex-port の
  // `ALF_COVERAGE_START`（= 2026-06-01）を流用したのが誤りで、あちらは「旧 sla_chats に
  // ALF完結分を UNION して安全か」を答える定数。sla_responses 単体のカバレッジの話ではない。
  // 実データは 2025-07 から連続していて、2026-06 に床を置くと 11ヶ月・judged 約10万件を捨てる。
  // 2026-10-07: **初動SLO も channel_talk_insights.sla_responses へ載せ替えた**
  // （`turn_index = 0` × `eff_response_time_sec <= 10`）。これで出どころは2本とも同じ表になるが、
  // ⚠ **それでも引き算できない**。初動は「1チャット単位・メール除外」、回答は
  // 「1ターン単位・メール込み」で、母集団も単位も違うまま。載せ替えで揃ったのは表だけ。
  // 2026-10-27: **API も `met_10s` を数えるだけにした**（それまでは「表に10秒の判定列が無い」
  // という理由で閾値比較を自前で書いており、判定が2箇所にあった）。同値は実測で確認済み
  // （2026-08 単月の分子 1,827 が完全一致）。⚠ API 側に閾値を書き戻さないこと。
  firstResponseSlaRate: {
    label: '初動SLO ｜ 10秒以内の初回応答',
    metricName: '初動SLO(10秒)',
    unit: 'percent',
    direction: 'up',
    goal: 1,
    // 今期SLO（2027年6月期）＝ 100%。2026-10-06 確定でゴールと同値。
    target: 1,
    aggregation: 'average',
    definition:
      'チャットの問い合わせ1件ごとに、お客様の最初の発話から10秒以内に最初の応答（AI・人を問わない）が返った割合。受付あいさつでも「応答」に数える（実回答かは問わない）。時計はカレンダー時間で、夜間・休日も止めない（KGIが「24時間365日いつでも」を掲げているため）。対象は medium=native のみ。メール・電話は定義上の対象外、Instagram（medium=app）は母数が小さく定義外。母集団は「お客様が開始したチャット」のみ（アウトバウンド配信への返信は含まない）。⚠ リグレッション検知の指標。下がったら対応の遅れではなく仕掛けの停止を先に疑う。読むときは pt ではなく未達の実数で見て、検知は週次の推移線で行う',
    baseline:
      '**99.8%（2026-08 実測 1,716/1,719・インバウンドのみ）**。channel_talk_insights.sla_responses を turn_index=0 × medium=native × is_inbound × 10秒で判定した値。月次（インバウンド・未達率/実数）は 2026-06 0.31%（7件）/ 07 0.25%（5）/ **08 0.17%（3・底）** / 09 0.47%（8）/ **2026-10 0.64%（12）**。🟡 **8月を底に3ヶ月連続で上がっている。「10月だけ跳ねた」ではない。** ⚠ 率では -0.4pt にしか見えないので**実数で見る**こと。⚠ ただし **2026-10 は部分月（27日時点）で、量そのものが戻っている**（n_all は 9月 63.6/日 → 10月 81.3/日）。**量が動く期間は実数だけでも歪むので、n・実数・率の3つを並べる**こと。原因未調査（CS 側で変更が続く想定のため調査は保留）。⚠ **2026-10-08 に 83.7% → 94.8% へ張り替えている**（旧 83.7% は Channel Talk の一括エクスポート由来で母集団も出どころも別物。11.1pt 上がったが改善ではない）。⚠ **下の月次15ヶ月はすべて #5150 以前の値**で、2026-08 だけ post-fix に差し替えてある。系列として使うときは全月を取り直すこと。月次（15ヶ月・pre-fix）は 2025-07 95.6% / 08 96.5% / 09 97.5% / 10 96.6% / 11 98.2% / 12 98.4% / 2026-01 97.6% / 02 91.0% / 03 92.2% / 04 95.0% / 05 94.1% / 06 95.1% / 07 94.4% / 08 94.8% / 09 96.4%。⚠ **この指標が測っているのは「何でもいいから即座に反応が返ったか」**で、達成の 95.1% は**すべて bot の受付あいさつ由来**（2026-06〜09 の 8,782 ターンで**人が10秒以内に返したものは1件も無い**）。定義どおり（受付あいさつでも応答に数える）で、壊れてはいない。⚠ **未達は「遅延」ではなく「bot が一度も出なかった」**（2026-06〜09 の未達 428 件のうち 425 件=99.3%）。しかも **428 件すべてが team_id 未割当のチャット**で、team_id が付いた 351 件の未達は 0 件。時間帯の偏りは弱い。導線（source_page / channel_id）は**既に Tier 2 に入っている**（2026-10-11 確認。「追加を依頼中」という以前の記述は解消済み）。⚠ **2026-10-13 に 94.8% → 93.7%（1,827/1,950）へ再張り替えした。-1.1pt は悪化ではなくバグ修正**（#5150 でシステムイベントを母集団から外した post-fix 値・本番テーブル実測）。✅ **2026-10-27 にインバウンドのみへ切り替えた（`AND is_inbound`）。93.6% → 99.8%**（2026-08 単月: 合計 1,951/達成 1,827/未達 124 → インバウンド 1,719/1,716/3）。**改善ではなく定義変更**なので 93.7% と繋げない。**上がる理由は、未達 124 件のうち 121 件（97.6%）が非インバウンド側だから**。⚠ 旧ベースライン 93.7%（1,827/1,950）とは**分子 1,827 が完全一致**し、分母だけ 1 件差（測定時点の差）。これで **API の自前閾値比較と Job の `met_10s` が同値であることも確認できた**。⚠ **未達 23 件はノイズではない**: 応答ゼロ 3件 / 4日放置 1件 / 翌朝対応 6件 / 中間 6件 / 10分以内に人が対応 7件。真の放置は実質4件だが、**ここが初動SLO が本来見たかった対象**。機序では bot 不発 15件 / bot が11〜17秒 7件（閾値10秒に対し bot p50 が6〜7秒で余裕3〜4秒しかない）/ bot が303秒 1件',
    source:
      'quality-atelier-kpi-design.md §3-0 / SLOへの改称 2026-10-05・今期SLOを100%に確定 2026-10-06・sla_responses へ載せ替え 2026-10-07・リグレッション検知と位置づけを確定 2026-10-13・インバウンドのみへ切り替え 2026-10-27。⚠ 検知は週次の推移線で見る（1日障害で月次 -3.3pt / 週次 -14pt。月次は鈍い）',
    decidedAt: '2026-10-06',
    pending:
      '✅ インバウンドのみへの切り替えは実装済み（2026-10-27）。扱いも確定済み（2026-10-13: KPI のまま残す／今期SLO 100% 据え置き／位置づけはリグレッション検知）。残件なし（ベースラインは 2026-08 単月の実測 1,716/1,719 へ張り替え済み）。🟡 **未達は 2026-08 を底に3ヶ月連続で上昇中**（0.17% → 0.47% → 0.64%）。CS 側で ALF・ワークフローの変更が続く想定のため**原因調査は保留**し、リグレッション検知の指標として推移を見る方針（2026-10-27・鷲澤）。🔵 **外れた 1,161 件（未達 546）を見る指標は無い。別指標は立てないと決めている**（2026-10-13・依頼1 の結論。未達 569 のうち 565 件＝99.3% はあとで有人が出ており返信の中央値は 9分、真の放置は 4 件）。**意図的な盲点**なので、見えなくなったことを忘れないこと',
  },
  // 2026-09-25 世代交代。旧「回答SLA｜2時間以内の実回答率」（営業時間クロック・目標95%）を
  // カレンダー時間へ置き換えた。さらにその前の世代は「解決SLA｜24時間解決率」。
  // 3世代とも別物なので推移を繋げない。
  // 2026-09-29: キー名を旧 resolutionSlaRate から answerSlaRate へ改名した。
  // 同時に追加した resolutionRate（解決率）と紛らわしく、取り違えると静かにずれるため。
  // 2026-10-05: SLA → SLO へ改称し、2026-10-04 に入れた「目標100%」を取り下げた
  // （理由と goal / target の分け方は初動SLO側のコメントに書いてある）。
  // 2026-10-04: 閾値を 5分 → 1分未満（59秒）へ引き下げた。
  // 閾値は状態ゴール文が 2026-10-02 に「59秒以内に返信がきて」へ変わったのに合わせたもの。
  // 判定定義そのものは変えていない（実効応答時間 EFF＝min(有人返信, ALF一次応答)・カレンダー時間・
  // 受付あいさつ/保留テンプレ/ワークフロー自動応答は回答に数えない）。動いたのは閾値だけ。
  // ⚠ 分では 59 秒を表せないため、実装側の定数は秒で持つ（ダッシュボードのコネクタは
  // RESPONSE_TARGET_SEC = 59）。表記は「1分未満（59秒）」で統一する。
  // ⚠ 5分世代の値と推移を繋げない。閾値が約5倍厳しくなるので必ず下がる。
  // ⚠ ベースラインの出どころも同時に変わった（エクスポート → channeltalk.sla_responses）。
  // 2026-10-07: ベースラインを **69.8% → 74.0% へ張り替えた**。出どころを凍結した旧
  // channeltalk.sla_responses から配線先の channel_talk_insights.sla_responses へ移したため。
  // ⚠ **同じ 2026-08 が2段動いている。** 閾値 5分→59秒 で 82.5%→69.8%（2026-10-04）、
  // 母集団の違いで 69.8%→74.0%（2026-10-07）。後者は判定ロジックが同一なので**改善ではない**。
  // 2026-10-07: 定義文を「問い合わせ1件ごと」→「**お客様が問いかけた1ターンごと**」へ直した。
  // 実装も数字も据え置き（旧 69.8% も新 74.0% も Ryan さんの突合値もすべてターン単位で、
  // 定義文だけが「1件ごと」と読めていた）。2026-08 で chat 3,510 に対し判定 3,982 ターン。
  // あわせて「メールを含む（電話は対象外）」を明記した。**初動SLO はメールを外す**ので、
  // 2本は単位も媒体も違う＝引き算できない。
  answerSlaRate: {
    label: '回答SLO ｜ 1分未満の実回答',
    metricName: '回答SLO(1分未満)',
    unit: 'percent',
    direction: 'up',
    goal: 1,
    // 今期SLO（2027年6月期）＝ 100%。2026-10-06 確定で初動SLOと同じ扱い。
    target: 1,
    aggregation: 'average',
    definition:
      'チャットでお客様が問いかけた1ターンごとに、その発話から1分未満（59秒以内）に「実際の回答」が返った割合。受付あいさつ・保留テンプレ・ワークフロー自動応答・ALFの降参は回答に数えず、ALFが答えられた回答だけを数える。時計はカレンダー時間。対象は medium=native のみ（メール・電話は対象外、Instagram（medium=app）は定義外）。初動SLOとは別分布で、P90は初動9分に対し6.3時間',
    baseline:
      '**70.5%（2026-08 実測 n=3,703）**。channel_talk_insights.sla_responses の `met_sla_answer` を medium=native で数えた値。⚠ **2026-10-27 に 74.2% → 70.5% へ張り替えた。改善でも悪化でもなく定義変更**（「回答に数えない」4種の除外が入った #5420 の列へ切り替え）。**率では -3.7pt だが実数では未達 955 → 1,092 件（+137件・14%増）**で、打ち手の優先順位は実数で見る。月次実測: 2026-06 73.7%（met_sla 76.9%）/ 07 73.5%（75.4%）/ 08 **70.5%**（74.2%）/ 09 72.8%（77.5%）/ 10 71.5%（79.0%）。⚠ **以前共有された 32.9% とは繋げない**（あれは150文字の下限を掛けた版で、確定定義では下限を外した）。以下は旧 met_sla 時代の経緯: 実効応答時間 EFF＝min(有人返信, ALF一次応答) を 59秒・カレンダー時間・medium=native で判定した値。⚠ **3段で動いている。どれも改善ではない**: 74.0%（n=4,664・native+email+app）→ 75.1%（n=4,577・Instagram 除外 #5086）→ 77.1%（n=3,787・メールも除外 #5113・ここまでは**定義外を母数から外しただけ**）→ **74.2%（n=3,703・システムイベント除外 #5150）**。最後の -2.9pt だけは母集団を狭めたのではなく、**本文を持たない操作ログを「人が応答した」と数えていたバグの修正**（2026-10-13 に本番テーブル実測で張り替え）。⚠ **下の月次15ヶ月はすべて #5150 以前の値**（2026-08 の 77.1% も pre-fix で、post-fix は 74.2%）。系列として使うときは全月を取り直すこと。月次（native のみ・15ヶ月・pre-fix）は 2025-07 71.9% / 08 75.5% / 09 78.7% / 10 79.1% / 11 82.9% / 12 80.3% / 2026-01 79.8% / 02 74.6% / 03 74.1% / 04 76.6% / 05 73.7% / 06 78.2% / 07 78.8% / 08 77.1% / 09 78.5%。⚠ **傾きの解釈は保留**。2025-07〜09 は取り込み開始直後で床（TIER2_COVERAGE_START=2025-07-01）に接しており、n も他月の半分以下。2026-10-07 に暫定テーブルを見て「4ヶ月で 5.2pt 下落」と書いて撤回しているので、**n が安定した区間だけで語ること**。⚠ 旧 69.8%（凍結した channeltalk.sla_responses）と**推移で繋げない**。判定ロジックは同一だが母集団が違い、旧は自前エクスポートに載った chat しか見ていなかった（2026-08 で 3,510 chat 中 2,655 chat）。⚠ 初動SLO 83.7% は Channel Talk エクスポート由来で出どころが違ううえ単位も違うため、2本を引き算しない。旧 72.9%（5分・エクスポート由来）とは母集団も閾値も別物',
    source:
      'quality-atelier-kpi-design.md §3-0 / 閾値の引き下げ 2026-10-04・今期SLOを100%に確定 2026-10-06・ベースラインを新テーブルへ張り替え 2026-10-07・native のみへ揃えて 77.1% に再張り替え 2026-10-08・システムイベント除外後の 74.2% へ張り替え 2026-10-13・met_sla_answer へ切り替えて 70.5% に張り替え 2026-10-27',
    decidedAt: '2026-10-06',
    // 2026-10-07: channel_talk_insights.sla_responses への接続が済み、実データになった。
    // 残るのは文面による除外。sla_responses の判定は person_type しか見ていないので、
    // 受付あいさつ・保留テンプレ・ワークフロー自動応答も**現状どれも「回答」として
    // 数えられている**。実測値は定義より高めに出ている可能性がある（ベースライン 74.0% も
    // 同じ経路なのでベースラインとの比較は成立する）。
    // 2026-10-08: 除外対象に **ALFの降参**を追加（CS確定）。`alf_type` は
    // `complete / rag`（答えられた）と `incomplete / impossible / failed`（降参）の2値に畳める。
    // 3つとも「AIが答えられなかった」で、転送はその後の処理にすぎない。
    // ⚠ いまの実装は ALF の発話を一律「応答」として数えるので、「お調べできませんでした」を
    // 59秒以内に返したターンが達成に入っている。**除外が入ると数字は下がる**。
    // 2026-10-08: 媒体を native + email に絞り（Instagram = medium 'app' は定義外）、
    // **同日さらに email も外して native のみにした**（CS 確認）。それまでメールを含めていたのは
    // 「定義に『メール・電話は対象外』の明記が無い」「旧 KPI ビューがそうだった」という消極的な
    // 理由で、CS がメールを入れると決めた記録は無かった。定義文自体が「チャットで」と始まりながら
    // メールを含む矛盾を抱えており、他の CS KPI 3本はいずれも「メール・電話は対象外」。
    // **回答SLO だけが例外だった**ので揃えた。59秒をメールに課すのも実態に合わない
    // （メールの SLA はもともと2時間）。⚠ 母集団が狭まるので**数字は動く。改善ではない**。
    // ⚠ これで媒体は初動SLO と同じになったが、**単位（1チャット / 1ターン）が違うので
    // 依然として引き算できない**。媒体で見分けられなくなったぶん注意すること。
    // ⚠ 2026-10-07 訂正: 同日いったん「判別に要る alf_type が Tier 2 に無いので実装できない」
    // と書いたが**誤り**。ALF_MESSAGE_COLUMNS は Tier 1 と Tier 2 の両方へ同一定義で
    // 入っている。有人の保留テンプレは body_text（これも Tier 2）の文面マッチで切れる。
    // つまり**Tier 2 の追加なしで実装できる**。判別は日次 Job 側へ入れること。
    // ⚠ 2026-10-08 再訂正: 「bot 発話が ALF 由来かは alf_session_id IS NOT NULL で切れる」も
    // **誤り**だった。alf_session_id は **Tier 1 でも常に NULL**（2026-09 以降の bot 58,053 件
    // すべて）。マッパーは alf.alfSessionId を読んでいるが API が返していない。正しいのは
    // **alf_mention_answered IS NOT NULL**（マッパーが alf === undefined ? null : Boolean(...)
    // なので、NOT NULL が「alf オブジェクトが存在したか」＝ ALF 由来かを表す）。
    // 実測は bot 58,053 のうち ALF 由来 2,357 / 受付あいさつ・ワークフロー自動応答 55,696。
    // ⚠ alf_type の語彙は 2025-11 前後で変わっており、以降は「答えられた＝NULL / 降参＝incomplete」。
    // alf_references_json IS NULL は降参の判定に使えない（system / openUserChat も refs 無し）。
    pending:
      '✅ 「回答に数えない」4種の除外は実装・切り替え済み（#5420 で met_sla_answer を追加 → 2026-10-27 に API を切り替え）。残件は2つ。①16ヶ月の月次を「合算」で取り直す（受領済みの系列は source_page あり＝お客様起点のみで出ており回答SLOの母集団と違う。データ基盤へ依頼済み）。②59秒という閾値の妥当性。⚠ **met_sla との差は 3.7pt（未達 955→1,092）しかなく、met_sla=TRUE の 95.0% が met_sla_answer も通る**。回答 p50 11秒に対し人の応答 p50 612秒なので、59秒は実質「ALFが答えたか/人が出たか」の2値になっている。判別力が小さいのは定義ではなく閾値側の問題で、**2026-10-27 時点では開かない判断**（鷲澤）',
  },
  // 2026-10-04: 判定を「3ターム以内**かつ**解決」に戻した。CSタスクフォースの整理完了版で
  // 「全チャット対象で3ターム以内の解決」と確定したため、2026-09-29 の分解を一部撤回している。
  // ⚠ 同名で中身の違う指標が3世代ある。
  //      2026-09-25〜29    3ターム以内かつ解決          65.1%
  //      2026-09-30〜10-04 3ターム以内のみ（解決問わず） 76.2%
  //      2026-10-04〜      3ターム以内かつ解決          65.1%  ← 現行
  //    **1世代目と現行は同じ定義なので 65.1% どうしは繋げてよい。76.2% とは繋げない。**
  // 母集団の4分割（全期間実測）: 3ターム以内かつ解決 65.1% / 4ターム以上だが解決 20.5% /
  // 3ターム以内だが未解決 11.1% / 両方未達 3.4%。
  // 「短く終わったが解決していない」11.1% は **解決率の未達 14.4pt（＝11.1 + 3.4）に含まれる**
  // ので、指標から消えたわけではない。失われたのは **層として切り分けて見ること**
  // （未達14.4ptのうちどちらが効いているか区別できない）。切り分けたくなったら別途モニタリングする。
  // なお旧76.2%はこの11.1%を達成として数えていたため「早く打ち切る」ゲーミングに弱く、
  // 現行はその弱点が無い（未解決で短く終えてもどちらのKPIでも達成にならない）。
  // ⚠ 状態ゴール文の分解としては崩れる。この指標は「3往復以内のやりとり」と「解決できる」の
  //    2節にまたがり resolutionRate と重なる。分解の整合より名前と実態の一致を優先した。
  threeTurnRate: {
    label: '3ターム解決率（チャット）',
    metricName: '3ターム解決率',
    unit: 'percent',
    direction: 'up',
    // 2026-10-05: KPI4本すべて同じ扱いに揃えた（理想 goal と今期の運用目標 target を分離）。
    // 2026-10-06: **今期SLOを4本とも 100% に確定**したので、goal と target は同値になった。
    // 分離の形は残してある（段階目標を置きたくなったら target 1行で切り替わる）。
    goal: 1,
    target: 1,
    aggregation: 'average',
    definition:
      'チャットの問い合わせ1件ごとに、お客様の実質的な発話3回以内で解決して終わった割合。ターム数と解決の両方を満たすこと。ターム＝ボタン/メニュー選択を除いたお客様の発話。AI完結と有人対応を区別せず同じ基準で判定する（全チャットが対象）。解決判定は resolutionRate と共通（2026-09-24 CS確定）。メール・電話は対象外。スタイリスト向けALFは母集団から除く',
    baseline:
      '65.1%（2026-01-01〜09-22 実測 n=20,432・月次レンジ 61.9〜67.3% で横ばい）。AI完結分64.5% / 有人対応分65.5% で差がほぼ無い。逆算ではなく直接計測値。未達 34.9pt の内訳は 4ターム以上だが解決 20.5% / 3ターム以内だが未解決 11.1% / 両方未達 3.4%',
    source:
      'quality-atelier-kpi-design.md §3-0 / 判定の戻し（2026-10-04）・今期SLOを100%に確定（2026-10-06）',
    decidedAt: '2026-10-06',
    pending:
      '✅ BigQuery 配線済み（2026-10-27・`channel_talk_insights.chat_resolutions` の `met_three_turn` を数えるだけ）。⚠ **ベースライン 65.1% はエクスポート由来の実測値**で、配線後の画面の値とは母集団の取り方が違う可能性がある（配線側は medium=native かつ exclusion_reason IS NULL で、`stylist_alf` / `no_real_question` もここで外れる）。実データの月次が出たら突き合わせて張り替えること',
  },
  // 2026-09-29 追加。状態ゴールの「解決できる」に対応する。アンケートではなく
  // お客様の行動で判定するため、CSAT を配信しなくても全チャットを母数にできる。
  resolutionRate: {
    label: '解決率（チャット）',
    metricName: '解決率',
    unit: 'percent',
    direction: 'up',
    goal: 1,
    // 今期SLO（2027年6月期）＝ 100%。2026-10-06 確定で3ターム解決率と同じ扱い。
    target: 1,
    aggregation: 'average',
    definition:
      'チャットの問い合わせ1件ごとに、お客様が納得して終わった割合。ターム数は問わない。解決は「AIが降参したまま沈黙」「回答後に聞き直して終了」「有人希望を出したのに有人返信ゼロ」「最後の発話に誰も応答していない」のいずれにも該当しないこと（2026-09-24 CS確定）。AI完結と有人対応を区別せず同じ基準で判定する。メール・電話は対象外。スタイリスト向けALFは母集団から除く',
    baseline:
      '85.6%（3ターム以内かつ解決 65.1% ＋ 4ターム以上かかったが解決 20.5%）。未達 14.4pt の内訳は 3ターム以内だが未解決 11.1% ＋ 両方未達 3.4%。AI完結分と有人対応分で差が無い（3ターム以内かつ解決で 64.5% / 65.5%）。⚠ 公開済みの内訳からの逆算値で、直接計測していない',
    source:
      'quality-atelier-kpi-design.md §3-0 / CS 指標構成の整理（2026-09-29）・今期SLOを100%に確定（2026-10-06）',
    decidedAt: '2026-10-06',
    pending:
      '✅ BigQuery 配線済み（2026-10-27・`chat_resolutions` の `resolved` を数えるだけ）。🔴 **これで 85.6% の逆算が実測に置き換わる**ので、画面の値は動く。改善でも悪化でもなく「初めて直接測った」。⚠ ベースラインは実データの月次が出てから張り替えること',
  },
  // 2026-09-29: KPI からモニタリングへ移した。状態ゴール文のどの節にも対応しておらず、
  // 「AI/人に関係なく」を **どう実現するかの手段** であって達成すべき状態ではないため。
  // ⚠ 目標値は置かない。上げること自体を目的化すると降参率が上がり解決率を壊す。
  // ⚠ 上限も固定ではない。人が対応する必要のあった問い合わせ自体が減れば分母が縮み、上限は上がる。
  aiCompletionRate: {
    label: 'AI完遂率',
    metricName: 'AI完遂率',
    unit: 'percent',
    direction: 'up',
    goal: null,
    target: null,
    aggregation: 'average',
    definition:
      'チャットの問い合わせのうち、ALFが起動し有人の公開返信がないまま終わった割合。KPIではなくモニタリング指標で、目標値は置かない。上げ方を誤ると壊れるため「AIが降参したまま終わった率」（現在10.7%）と在籍6ヶ月以上の離脱率を必ず併せて見る。メール・電話は対象外',
    baseline:
      '40.5%（2026-01-01〜09-22 実測 8,458/20,895・月次レンジ 35.6〜45.4%）。上げしろは「AIが答えを持っていたのに人が出た」5,161件で、うちユーザーが有人を希望していない2,641件はそのまま取れる（→53.1%）。有人対応を1,000件なくすごとに約2.7pt上がる',
    source: 'quality-atelier-kpi-design.md §3-0 / CS 指標構成の整理（2026-09-29）',
    decidedAt: '2026-09-29',
    pending: '在籍6ヶ月以上の離脱率の計測（ガードレール）／BigQuery 接続',
  },
};

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 指定KPIの定義を取得する
 * @graph-connects none
 */
export function kpiDef(id: KpiId): KpiDefinition {
  return KPI_DEFINITIONS[id];
}
